import React, { useEffect, useRef, useState } from "react";
import { Badge, Button, Card, Col, Container, Modal, OverlayTrigger, Row, Table, Tooltip } from "react-bootstrap";
import { useMutation, useQuery } from "@apollo/client";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSyncAlt } from "@fortawesome/free-solid-svg-icons";
import { toast } from "react-toastify";
import { Loader, Message } from "../../components";
import { GET_FLAT_TABLE_REFRESH_STATUS } from "../../graphql/queries";
import { TRIGGER_FLAT_TABLE_REFRESH } from "../../graphql/mutations";
import { useAppContext } from "../../context/appContext";
import { isViewOnly } from "../../utils/permissions";

const STATUS_LABEL = {
  IMPORT_DONE: "Import Done",
  IMPORT_FAILED: "Import Failed",
  IMPORT_IN_PROGRESS: "Import In progress"
};

const STATUS_VARIANT = {
  IMPORT_DONE: "success",
  IMPORT_FAILED: "danger",
  IMPORT_IN_PROGRESS: "warning"
};

// The API has no status to report until the flat table has been refreshed at least once.
const NO_STATUS_LABEL = "Idle";
const NO_STATUS_VARIANT = "secondary";

const REFRESHABLE_STATUSES = ["IMPORT_DONE", "IMPORT_FAILED"];

const DATE_OPTIONS = {
  day: "2-digit",
  month: "short",
  year: "numeric",
  hour: "2-digit",
  minute: "2-digit",
  hour12: false
};

export const START_WAIT_MS = 90000;
const TOAST_MS = 3000;
const toastOptions = { autoClose: TOAST_MS };
export const DID_NOT_START_MESSAGE = "Update did not start. The latest data was not refreshed.";
export const UPDATE_FAILED_MESSAGE = "Update failed";
export const FAILED_TO_START_MESSAGE = "Failed to start update";

const isRunningStatus = (status) => Boolean(status?.inProgress) || status?.status === "IMPORT_IN_PROGRESS";

const isNewJob = (status, dateAtTrigger) => Boolean(status) && status.dateCreated !== dateAtTrigger;

const getLocalTimeZone = () => Intl.DateTimeFormat().resolvedOptions().timeZone || "Local";

const formatInTimeZone = (iso, timeZone) =>
  new Date(iso).toLocaleString("en-GB", {
    ...DATE_OPTIONS,
    timeZone,
    timeZoneName: "short"
  });

const parseMessage = (message) => {
  if (!message) {
    return {};
  }
  if (typeof message === "object") {
    return message;
  }
  try {
    return JSON.parse(message);
  } catch {
    return { error: String(message) };
  }
};

const DateTimeCell = ({ iso }) => {
  if (!iso) {
    return "—";
  }

  const localTimeZone = getLocalTimeZone();

  return (
    <div>
      <div className="d-flex align-items-baseline gap-2">
        <span className="text-muted" style={{ minWidth: "48px", fontSize: "0.75rem" }}>
          UTC
        </span>
        <span>{formatInTimeZone(iso, "UTC")}</span>
      </div>
      <div className="d-flex align-items-baseline gap-2">
        <span className="text-muted" style={{ minWidth: "48px", fontSize: "0.75rem" }}>
          Local
        </span>
        <span>
          {formatInTimeZone(iso, localTimeZone)}
          <small className="text-muted ms-2">({localTimeZone})</small>
        </span>
      </div>
    </div>
  );
};

const FlatTableRefreshPage = ({ env }) => {
  const { user } = useAppContext();
  const viewOnly = isViewOnly(user, env);
  const [showConfirm, setShowConfirm] = useState(false);
  const [confirmReady, setConfirmReady] = useState(false);
  const [waitingToStart, setWaitingToStart] = useState(false);
  const statusDateAtTriggerRef = useRef(null);
  const statusRef = useRef(null);
  const confirmRequestRef = useRef(0);

  const { data, loading, error, refetch, networkStatus, startPolling, stopPolling } = useQuery(GET_FLAT_TABLE_REFRESH_STATUS, {
    fetchPolicy: "network-only",
    notifyOnNetworkStatusChange: true,
    errorPolicy: "all"
  });

  const [triggerRefresh, { loading: triggering }] = useMutation(TRIGGER_FLAT_TABLE_REFRESH, {
    onCompleted: (result) => {
      const payload = result?.triggerFlatTableRefresh;
      if (payload?.taskStatus === "success") {
        setWaitingToStart(true);
      } else {
        toast.error(payload?.message || payload?.error || FAILED_TO_START_MESSAGE, toastOptions);
        setWaitingToStart(false);
      }
      refetch();
    },
    onError: () => {
      toast.error(FAILED_TO_START_MESSAGE, toastOptions);
      setWaitingToStart(false);
    }
  });

  const status = data?.flatTableRefreshStatus;
  statusRef.current = status;
  const hasQueryError = Boolean(error);
  const message = parseMessage(status?.message);
  const errorText = message.error || null;
  const apiInProgress = isRunningStatus(status);
  const inProgress = !hasQueryError && apiInProgress;
  const refreshStatus = status?.status || null;
  const statusLabel = refreshStatus ? STATUS_LABEL[refreshStatus] || refreshStatus : NO_STATUS_LABEL;
  const statusVariant = (refreshStatus && STATUS_VARIANT[refreshStatus]) || NO_STATUS_VARIANT;
  const isFailed = refreshStatus === "IMPORT_FAILED" && !inProgress;
  const canRefresh =
    !viewOnly && !inProgress && !waitingToStart && !triggering && (refreshStatus === null || REFRESHABLE_STATUSES.includes(refreshStatus));
  const canConfirm = canRefresh && confirmReady && !hasQueryError;
  const statusRefreshing = (loading || networkStatus === 4) && !hasQueryError;
  const iconBusy = !hasQueryError && (statusRefreshing || inProgress || waitingToStart);
  const reloadStatusHint = inProgress
    ? "Status is updating automatically while the data update is in progress."
    : waitingToStart
      ? "Waiting for the update to start."
      : statusRefreshing
        ? "Status is reloading. Please wait."
        : "Click to reload the current status. This does not start a data update.";

  const invalidateConfirmRequest = () => {
    confirmRequestRef.current += 1;
    setConfirmReady(false);
  };

  useEffect(() => {
    if (inProgress) {
      invalidateConfirmRequest();
      setShowConfirm(false);
    }
  }, [inProgress]);

  useEffect(() => {
    if (!waitingToStart || !status) {
      return;
    }
    const jobChanged = isNewJob(status, statusDateAtTriggerRef.current);
    if (apiInProgress) {
      setWaitingToStart(false);
      if (jobChanged) {
        toast.success("Update started", toastOptions);
      }
      return;
    }
    if (!jobChanged) {
      return;
    }
    setWaitingToStart(false);
    if (status.status === "IMPORT_FAILED") {
      toast.error(errorText || UPDATE_FAILED_MESSAGE, toastOptions);
    }
  }, [waitingToStart, status, apiInProgress, errorText]);

  useEffect(() => {
    if (!waitingToStart) {
      return;
    }
    const timeoutId = setTimeout(() => {
      const current = statusRef.current;
      if (isRunningStatus(current) || isNewJob(current, statusDateAtTriggerRef.current)) {
        setWaitingToStart(false);
        return;
      }
      toast.error(DID_NOT_START_MESSAGE, toastOptions);
      setWaitingToStart(false);
    }, START_WAIT_MS);
    return () => clearTimeout(timeoutId);
  }, [waitingToStart]);

  useEffect(() => {
    if (inProgress || waitingToStart) {
      startPolling(3000);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [inProgress, waitingToStart, startPolling, stopPolling]);

  const getDisabledReason = () => {
    if (canRefresh) {
      return null;
    }
    if (viewOnly) {
      return "You do not have permission to update data.";
    }
    if (waitingToStart || triggering) {
      return "Waiting for the update to start.";
    }
    if (inProgress) {
      return "An update is in progress. Please wait until it finishes.";
    }
    return null;
  };

  const disabledReason = getDisabledReason();

  const handleCloseConfirm = () => {
    invalidateConfirmRequest();
    setShowConfirm(false);
  };

  const handleOpenConfirm = () => {
    const requestId = ++confirmRequestRef.current;
    setShowConfirm(true);
    setConfirmReady(false);
    refetch().finally(() => {
      if (requestId === confirmRequestRef.current) {
        setConfirmReady(true);
      }
    });
  };

  const handleConfirmRefresh = () => {
    if (!canConfirm) {
      return;
    }
    invalidateConfirmRequest();
    setShowConfirm(false);
    statusDateAtTriggerRef.current = status?.dateCreated ?? null;
    triggerRefresh();
  };

  const getButtonLabel = () => {
    if (inProgress) {
      return "Update in progress...";
    }
    if (waitingToStart || triggering) {
      return "Starting...";
    }
    return "Refresh Data";
  };

  if (loading && !status && !hasQueryError) {
    return (
      <Container className="py-4">
        <Loader />
      </Container>
    );
  }

  return (
    <Container fluid className="py-3">
      <Row className="mb-3">
        <Col>
          <div className="d-flex align-items-center gap-2">
            <h4 className="mb-0">Flat table status</h4>
            <OverlayTrigger placement="right" overlay={<Tooltip id="reload-status-tooltip">{reloadStatusHint}</Tooltip>}>
              <span className="d-inline-flex">
                <Button variant="link" className="p-0" onClick={() => refetch()} disabled={iconBusy} aria-label={reloadStatusHint}>
                  <FontAwesomeIcon icon={faSyncAlt} spin={iconBusy} />
                </Button>
              </span>
            </OverlayTrigger>
          </div>
          <p className="text-muted mb-0 mt-1">
            This page shows the latest data update.
            {status?.nextScheduledAt
              ? ` Next automatic update: ${formatInTimeZone(status.nextScheduledAt, "UTC")} (UTC).`
              : " An automatic update runs hourly (UTC)."}{" "}
            How long it takes to finish depends on the current data volume. Use the button below if you need to update it now. Times are
            shown in UTC and your local timezone.
          </p>
        </Col>
      </Row>

      {hasQueryError && <Message variant="danger">Could not load the latest status. Click the reload icon to try again.</Message>}

      <Card>
        <Card.Body>
          <Table bordered responsive className="table-sm mb-3">
            <tbody>
              <tr>
                <th style={{ width: "220px" }}>Status</th>
                <td>
                  <Badge bg={statusVariant}>{statusLabel}</Badge>
                </td>
              </tr>
              {isFailed && errorText && (
                <tr>
                  <th>Details</th>
                  <td className="text-danger" data-testid="refresh-error-details">
                    {errorText}
                  </td>
                </tr>
              )}
              <tr>
                <th>Last update</th>
                <td>
                  <DateTimeCell iso={status?.lastRefreshAt} />
                </td>
              </tr>
              <tr>
                <th>Next automatic update</th>
                <td>
                  <DateTimeCell iso={status?.nextScheduledAt} />
                </td>
              </tr>
              <tr>
                <th>Rows updated</th>
                <td>{status?.rowsAffected ?? "—"}</td>
              </tr>
              <tr>
                <th>Duration</th>
                <td>{status?.durationSeconds != null ? `${status.durationSeconds}s` : "—"}</td>
              </tr>
            </tbody>
          </Table>

          <Button variant="primary" disabled={!canRefresh} onClick={handleOpenConfirm}>
            {getButtonLabel()}
          </Button>
          <small className="text-muted d-block mt-2">
            Updates the latest data now. The button stays unavailable until the update finishes. Duration depends on the current data
            volume.
          </small>
          {disabledReason && (
            <small className="text-muted d-block mt-2" data-testid="refresh-disabled-reason">
              {disabledReason}
            </small>
          )}
        </Card.Body>
      </Card>

      <Modal show={showConfirm} onHide={handleCloseConfirm} centered animation={false}>
        <Modal.Header closeButton>
          <Modal.Title>Refresh Data</Modal.Title>
        </Modal.Header>
        <Modal.Body>This will update the latest data now. The button will stay unavailable until the update finishes. Continue?</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseConfirm}>
            Cancel
          </Button>
          <Button variant="primary" onClick={handleConfirmRefresh} disabled={!canConfirm}>
            Yes, update
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default FlatTableRefreshPage;
